type RangeBarProps = {
  goodRange: [number, number];
  warningRange: [number, number];
  currentValue?: number | null;
  label?: string;
  unit?: string;
  min?: number;
  max?: number;
};

const GREEN = "#1bac4b";
const ORANGE = "#fb9400";
const RED = "#f65554";

const statusFor = (
  value: number,
  goodRange: [number, number],
  warningRange: [number, number]
) => {
  if (value >= goodRange[0] && value <= goodRange[1]) {
    return { label: "Good", color: GREEN };
  }
  if (value >= warningRange[0] && value <= warningRange[1]) {
    return { label: "Warning", color: ORANGE };
  }
  return { label: "Critical", color: RED };
};

export const RangeBar = ({
  goodRange,
  warningRange,
  currentValue,
  label,
  unit = "",
  min,
  max,
}: RangeBarProps) => {
  const actualMin = min ?? Math.min(warningRange[0], goodRange[0]);
  const actualMax = max ?? Math.max(warningRange[1], goodRange[1]);
  const range = Math.max(actualMax - actualMin, 1);

  const pct = (value: number) => ((value - actualMin) / range) * 100;
  const goodStart = pct(goodRange[0]);
  const goodEnd = pct(goodRange[1]);
  const warningStart = pct(warningRange[0]);
  const warningEnd = pct(warningRange[1]);

  const hasValue =
    currentValue !== null && currentValue !== undefined && !Number.isNaN(currentValue);
  const status = hasValue
    ? statusFor(currentValue, goodRange, warningRange)
    : null;
  const currentPosition = hasValue
    ? Math.max(0, Math.min(100, pct(currentValue)))
    : null;

  return (
    <div className="range-bar">
      {label ? (
        <div className="range-bar-header">
          <span className="range-bar-label">{label}</span>
          {hasValue && status ? (
            <div className="range-bar-value-wrap">
              <span className="range-bar-value" style={{ color: status.color }}>
                {currentValue}
                {unit ? ` ${unit}` : ""}
              </span>
              <span
                className="range-bar-badge"
                style={{
                  color: status.color,
                  background: `${status.color}20`,
                }}
              >
                {status.label}
              </span>
            </div>
          ) : (
            <span className="range-bar-empty">No reading yet</span>
          )}
        </div>
      ) : null}

      <div className="range-bar-track-wrap">
        <div className="range-bar-track">
          {warningStart < goodStart ? (
            <span
              className="range-bar-segment"
              style={{
                left: `${warningStart}%`,
                width: `${goodStart - warningStart}%`,
                background: `${ORANGE}40`,
              }}
            />
          ) : null}
          <span
            className="range-bar-segment"
            style={{
              left: `${goodStart}%`,
              width: `${goodEnd - goodStart}%`,
              background: `${GREEN}40`,
            }}
          />
          {warningEnd > goodEnd ? (
            <span
              className="range-bar-segment"
              style={{
                left: `${goodEnd}%`,
                width: `${warningEnd - goodEnd}%`,
                background: `${ORANGE}40`,
              }}
            />
          ) : null}
        </div>
        {currentPosition !== null && status ? (
          <span
            className="range-bar-thumb"
            style={{
              left: `${currentPosition}%`,
              background: status.color,
            }}
            aria-hidden
          >
            <span className="range-bar-thumb-dot" />
          </span>
        ) : null}
      </div>

      <div className="range-bar-legend">
        <span className="range-bar-bound">{actualMin}</span>
        <div className="range-bar-legend-items">
          <span className="range-bar-legend-item">
            <span className="range-bar-swatch" style={{ background: GREEN }} />
            Good: {goodRange[0]} – {goodRange[1]}
          </span>
          <span className="range-bar-legend-item">
            <span className="range-bar-swatch" style={{ background: ORANGE }} />
            Warning: {warningRange[0]} – {warningRange[1]}
          </span>
        </div>
        <span className="range-bar-bound">{actualMax}</span>
      </div>
    </div>
  );
};
